import { useMemo, useRef, useState } from "react";
import { apiCreate, apiDelete, apiList, apiUpdate } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { usePacientes } from "@/hooks/usePacientes";
import { useDentistas } from "@/hooks/useDentistas";
import { useActiveClinicaId } from "@/hooks/useActiveClinicaId";
import { parseLocalDateTime } from "@/lib/date-utils";
import { queueCommunicationEvent } from "@/lib/campaigns";

export interface Agendamento {
  id: string;
  paciente_id: string;
  dentista_id: string;
  data_agendamento: string;
  hora?: string;
  duracao: number;
  procedimento: string;
  status: string;
  tipo_atendimento: string;
  convenio_id?: string;
  valor?: number;
  observacoes?: string;
  confirmado: boolean;
  created_at?: string;
  updated_at?: string;
  pacientes?: { nome: string; telefone: string; email: string };
  dentistas?: { nome: string };
  convenios?: { nome: string };
}

export const useAgendamentos = () => {
  const [rawAgendamentos, setRawAgendamentos] = useState<Agendamento[]>([]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const { pacientes, loading: loadingPacientes } = usePacientes();
  const { dentistas, loading: loadingDentistas } = useDentistas();
  const activeClinicaId = useActiveClinicaId();
  const fetchSequence = useRef(0);
  const activeClinicaIdRef = useRef(activeClinicaId);
  activeClinicaIdRef.current = activeClinicaId;

  const agendamentos = useMemo(
    () => rawAgendamentos.map((item) => {
      const paciente = pacientes.find((p) => p.id === item.paciente_id);
      const dentista = dentistas.find((d) => d.id === item.dentista_id);
      return {
        ...item,
        pacientes: paciente ? { nome: paciente.nome, telefone: paciente.telefone, email: paciente.email } : item.pacientes,
        dentistas: dentista ? { nome: dentista.nome } : item.dentistas,
      };
    }),
    [rawAgendamentos, pacientes, dentistas]
  );

  const fetchAgendamentos = async (startDate?: Date, endDate?: Date) => {
    const requestSequence = ++fetchSequence.current;
    const requestedClinicaId = activeClinicaId;
    try {
      setLoading(true);
      let data = await apiList<Agendamento>("agendamentos");
      if (startDate) data = data.filter((a) => parseLocalDateTime(a.data_agendamento) >= startDate);
      if (endDate) data = data.filter((a) => parseLocalDateTime(a.data_agendamento) <= endDate);
      data.sort((a, b) => String(a.data_agendamento).localeCompare(String(b.data_agendamento)));
      if (requestSequence !== fetchSequence.current || requestedClinicaId !== activeClinicaIdRef.current) return;
      setRawAgendamentos(data);
    } catch (error: any) {
      toast({ title: "Erro ao carregar agendamentos", description: error.message, variant: "destructive" });
    } finally {
      if (requestSequence === fetchSequence.current) setLoading(false);
    }
  };

  const createAgendamento = async (agendamento: Omit<Agendamento, "id" | "created_at" | "updated_at" | "pacientes" | "dentistas" | "convenios">) => {
    try {
      setLoading(true);
      await apiCreate<Agendamento>("agendamentos", agendamento);
      toast({ title: "Agendamento criado", description: "Agendamento cadastrado com sucesso!" });
      await fetchAgendamentos();
    } catch (error: any) {
      toast({ title: "Erro ao criar agendamento", description: error.message, variant: "destructive" });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const updateAgendamento = async (id: string, agendamento: Partial<Agendamento>) => {
    try {
      setLoading(true);
      const previous = rawAgendamentos.find((item) => item.id === id);
      const updated = await apiUpdate<Agendamento>("agendamentos", id, agendamento);
      const patient = pacientes.find((item) => item.id === updated.paciente_id);
      const dentist = dentistas.find((item) => item.id === updated.dentista_id);
      const eventType = previous?.status !== updated.status && updated.status === "Confirmado"
        ? "agendamento_confirmado"
        : previous?.status !== updated.status && updated.status === "Realizado"
          ? "procedimento_concluido"
          : previous?.status !== updated.status && updated.status === "Cancelado"
            ? "agendamento_cancelado"
            : previous?.data_agendamento && previous.data_agendamento !== updated.data_agendamento
              ? "agendamento_reagendado"
              : "";
      if (eventType && patient) {
        await queueCommunicationEvent({
          tipo_evento: eventType,
          clinica_id: activeClinicaId,
          paciente_id: patient.id,
          paciente_nome: patient.nome,
          email: patient.email,
          telefone: patient.telefone,
          titulo: updated.procedimento || "Consulta odontológica",
          procedimento: updated.procedimento || "Consulta odontológica",
          data: updated.data_agendamento.slice(0, 10),
          hora: updated.hora || updated.data_agendamento.slice(11, 16),
          dentista: dentist?.nome || "",
          origem_id: `${updated.id}:${updated.updated_at || updated.data_agendamento}:${eventType}`,
        }).catch(() => undefined);
      }
      toast({ title: "Agendamento atualizado", description: "Agendamento atualizado com sucesso!" });
      await fetchAgendamentos();
    } catch (error: any) {
      toast({ title: "Erro ao atualizar agendamento", description: error.message, variant: "destructive" });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const deleteAgendamento = async (id: string) => {
    try {
      setLoading(true);
      const appointment = rawAgendamentos.find((item) => item.id === id);
      const patient = appointment ? pacientes.find((item) => item.id === appointment.paciente_id) : undefined;
      await apiDelete("agendamentos", id);
      if (appointment && patient) await queueCommunicationEvent({
        tipo_evento: "agendamento_cancelado",
        clinica_id: activeClinicaId,
        paciente_id: patient.id,
        paciente_nome: patient.nome,
        email: patient.email,
        telefone: patient.telefone,
        titulo: appointment.procedimento,
        procedimento: appointment.procedimento,
        data: appointment.data_agendamento.slice(0, 10),
        hora: appointment.hora || appointment.data_agendamento.slice(11, 16),
        origem_id: `${appointment.id}:exclusao`,
      }).catch(() => undefined);
      toast({ title: "Agendamento excluido", description: "Agendamento excluido com sucesso!" });
      await fetchAgendamentos();
    } catch (error: any) {
      toast({ title: "Erro ao excluir agendamento", description: error.message, variant: "destructive" });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const confirmarAgendamento = async (id: string) => {
    await updateAgendamento(id, { confirmado: true, status: "Confirmado" });
  };

  return {
    agendamentos,
    loading: loading || loadingPacientes || loadingDentistas,
    activeClinicaId,
    fetchAgendamentos,
    createAgendamento,
    updateAgendamento,
    deleteAgendamento,
    confirmarAgendamento,
  };
};
